import PropTypes from 'prop-types'
import React from 'react'
import onClickOutside from 'react-onclickoutside'
import _ from 'lodash'
import { findDOMNode } from 'react-dom'
import scrollIntoView from 'scroll-into-view-if-needed'
import getCharCount from '../../utils/getCharCount'
import getCharCountFromEditorState from '../../utils/getCharCountFromEditorState'
import {
  convertKanBoFormatToContentState,
  convertEditorStateToKanBoFormat
} from '../../utils/convert'
import deepEqual from 'deep-equal'
import isPromise from 'is-promise'
import { EditorState, ContentState } from 'draft-js'
import { FormattedMessage } from 'react-intl'
import {
  Button,
  ButtonGroup,
  SplitButton,
} from 'react-bootstrap'
import 'draft-js/dist/Draft.css'
import TextareaAutosize from 'react-textarea-autosize'
import classes from './MobileEditor.module.scss'

export class MobileEditor extends React.Component {
  static propTypes = {
    unsavedValue: PropTypes.object,

    addUnsavedChange: PropTypes.func,
    removeUnsavedChange: PropTypes.func,

    content: PropTypes.object,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    mentionBoardId: PropTypes.number,
    autoFocus: PropTypes.bool,
    checklistBlockTypes: PropTypes.bool,
    className: PropTypes.string,
    maxLength: PropTypes.number,
    placeholder: PropTypes.string,
    saveChanges: PropTypes.string,
    expandOnFocus: PropTypes.bool,
    submitButton: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.string
    ]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onSubmit: PropTypes.func
  }

  static defaultProps = {
    expandOnFocus: false,
    submitButton: <FormattedMessage id='SAVE' />
  }

  constructor (props) {
    super(props)

    const editorState =
      props.unsavedValue
        ? EditorState.createWithContent(convertKanBoFormatToContentState(props.unsavedValue, { textOnly: true }))
        : props.content
          ? EditorState.createWithContent(convertKanBoFormatToContentState(props.content, { textOnly: true }))
          : EditorState.createEmpty()

    this.state = {
      editorState,
      content: convertEditorStateToKanBoFormat(editorState, { textOnly: true }),
      charCount: getCharCountFromEditorState(editorState),
      isFocused: !!this.props.autoFocus
    }

    this.blur = this.blur.bind(this)
    this.focusEditor = this.focusEditor.bind(this)
    this.clear = this.clear.bind(this)
    this.submit = this.submit.bind(this)
    this.handleEditorRef = this.handleEditorRef.bind(this)
    this.handleUnsavedChange = _.debounce(this.handleUnsavedChange.bind(this), 200)
    this.handleCharCount = _.throttle(this.handleCharCount.bind(this), 200)
    this.handleChange = this.handleChange.bind(this)
    this.handleEditorStateChange = this.handleEditorStateChange.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleEditorMouseDown = this.handleEditorMouseDown.bind(this)
    this.handleEditorFocus = this.handleEditorFocus.bind(this)
    this.handleEditorBlur = this.handleEditorBlur.bind(this)
    this.handleSubmitClick = this.handleSubmitClick.bind(this)
    this.handleCancelClick = this.handleCancelClick.bind(this)
  }

  componentDidMount () {
    if (this.props.autoFocus) {
      this.focusRequestedFrame = requestAnimationFrame(() => {
        this.focusEditor()
      })
    }

    if (this.state.isFocused) {
      this.scrollIntoView()
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (
      (this.state.isFocused && !prevState.isFocused) ||
      (this.state.isFocused && this.editorState !== prevState.editorState)
    ) {
      this.scrollIntoView()
    }
  }

  componentWillUnmount () {
    cancelAnimationFrame(this.focusRequestedFrame)
    cancelAnimationFrame(this.scrollRequestedFrame)

    this.handleCharCount.cancel()
  }

  scrollIntoView = () => {
    cancelAnimationFrame(this.scrollRequestedFrame)
    this.scrollRequestedFrame = requestAnimationFrame(() => {
      scrollIntoView(findDOMNode(this), {
        scrollMode: 'if-needed',
        block: 'nearest'
      })
    })
  }

  handleClickOutside (evt) {
    this.blur()
  }

  blur () {
    if (this.state.isFocused) {
      this.setState({
        isFocused: false
      })
    }
  }

  focusEditor () {
    this.editor.focus()
  }

  clear () {
    this.handleEditorStateChange(EditorState.push(this.state.editorState, ContentState.createFromText('')))
  }

  submit () {
    const { error, onSubmit } = this.props

    if (error) return

    return new Promise((resolve, reject) => {
      let result
      try {
        result = onSubmit(this.state.content)

        if (isPromise(result)) {
          result.then(resolve)
        } else {
          resolve()
        }
      } catch (submitError) {
        reject(submitError)
      }
    }).then(() => {
      if (this.props.error) return

      this.editor && this.editor.blur()

      this.clear()

      this.handleUnsavedChange.flush()
    })
  }

  handleEditorRef (ref) {
    this.editor = ref
  }

  handleUnsavedChange (content, currentContent) {
    const { saveChanges, addUnsavedChange, removeUnsavedChange } = this.props

    if (saveChanges) {
      if (getCharCount(content) > 0 && !deepEqual(content, currentContent)) {
        addUnsavedChange({
          id: saveChanges,
          value: content
        })
      } else {
        removeUnsavedChange({
          id: saveChanges
        })
      }
    }
  }

  handleCharCount (editorState) {
    this.setState({
      charCount: getCharCountFromEditorState(editorState)
    })
  }

  handleChange (event) {
    this.handleEditorStateChange(EditorState.push(this.state.editorState, ContentState.createFromText(event.target.value)))
  }

  handleEditorStateChange (editorState) {
    const { onChange } = this.props

    const content = convertEditorStateToKanBoFormat(editorState, { textOnly: true })

    this.handleUnsavedChange(content, this.props.content)

    this.handleCharCount(editorState)

    this.setState({
      editorState,
      content
    })

    onChange && onChange(content)
  }

  handleClick (event) {
    event.preventDefault()
    this.focusEditor()
  }

  handleEditorMouseDown (event) {
    event.stopPropagation()
  }

  handleEditorFocus (event) {
    if (!this.state.isFocused) {
      this.setState({
        isFocused: true
      })

      this.props.onFocus && this.props.onFocus()
    }
  }

  handleEditorBlur (event) {
    this.props.onBlur && this.props.onBlur()

    this.blur()
  }

  handleSubmitClick (event) {
    event.preventDefault()

    this.submit()
  }

  handleCancelClick (event) {
    event.stopPropagation()

    this.blur()
  }

  render () {
    const {
      submitting,
      className,
      maxLength,
      placeholder,
      expandOnFocus,
      submitButton,
      onSubmit,
      sendActions,
    } = this.props
    const {
      content,
      editorState,
      charCount,
      isFocused
    } = this.state

    let compClassName = classes.richTextEditor + ' form-control'
    if (isFocused) compClassName += ' focused'
    if (className) compClassName += ' ' + className

    return (
      <div
        className={compClassName}
        onMouseDown={this.handleClick}>
        <TextareaAutosize
          ref={this.handleEditorRef}
          className={classes.editor + ' form-control'}
          spellCheck='false'
          placeholder={placeholder}
          value={editorState.getCurrentContent().getPlainText()}
          onFocus={this.handleEditorFocus}
          onBlur={this.handleEditorBlur}
          onChange={this.handleChange}
          onMouseDown={this.handleEditorMouseDown}
        />
        {(isFocused || !expandOnFocus) &&
          <div className={classes.buttons}>
            {(onSubmit && submitButton) &&
              <ButtonGroup>
                {sendActions ? (
                  <SplitButton
                    bsStyle='primary'
                    disabled={submitting}
                    title={submitButton}
                    onMouseDown={this.handleSubmitClick}
                  >
                    {React.Children.map(sendActions, sendAction => {
                      if (!React.isValidElement(sendAction)) {
                        return null
                      }

                      return React.cloneElement(
                        sendAction,
                        {
                          onClick: event => {
                            this.handleCancelClick(event)

                            sendAction.props.onClick && sendAction.props.onClick(event, content, this.clear)
                          }
                        },
                      )
                    })}
                  </SplitButton>
                ) : (
                  <Button
                    bsStyle='primary'
                    disabled={submitting}
                    onMouseDown={this.handleSubmitClick}
                  >
                    {submitButton}
                  </Button>
                )}
                <Button
                  onMouseDown={this.handleCancelClick}
                >
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            }
            {maxLength &&
              <span className={classes.remainingCharacters}>
                {charCount}{'/'}{maxLength}
              </span>
            }
          </div>
        }
      </div>
    )
  }
}

export default onClickOutside(MobileEditor)

